import React from 'react';
import styled from 'styled-components';
import { FaFileAlt as FileIcon } from 'react-icons/fa';
import { format } from 'date-fns';
import {
  Flexbox,
  MediaLargerEqual,
  SubmissionCard as SubmissionCardBase
} from '@morressier/ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faDownload } from '@fortawesome/pro-regular-svg-icons';

import { ISubmissionSearchResult } from 'typings/submission';

export const SubmissionCardLink = styled.a<{ cardWidth?: string }>`
  cursor: pointer;
  text-decoration: none;
  outline: none;
  color: inherit;
  border-radius: 1rem;
  max-width: ${props => props.cardWidth ?? '64rem'};
`;

const IconWrapper = styled.span`
  padding: 4px;
  margin: 0 4px 0 0;
`;

interface Props {
  submissionId?: string;
  submission?: ISubmissionSearchResult | null;
  hideEventName?: boolean;
  currentPath?: string;
  logEvent?: () => void;
  cardWidth?: string;
}

const SubmissionCard = (props: Props) => {
  const {
    submission,
    hideEventName,
    submissionId = '',
    currentPath,
    cardWidth
  } = props;

  const downloads = (
    <Flexbox gap={4}>
      {submission?.downloadsCount || 0}
      <MediaLargerEqual breakpoint="xsmall">downloads</MediaLargerEqual>
    </Flexbox>
  );
  const views = (
    <Flexbox gap={4}>
      {submission?.viewsCount || 0}
      <MediaLargerEqual breakpoint="xsmall">views</MediaLargerEqual>
    </Flexbox>
  );

  const updatedAt = submission?.uploadedAt
    ? format(new Date(submission?.uploadedAt * 1000), 'MMM d, yyyy')
    : '';

  const contentTag = {
    tag: submission?.eventName || hideEventName ? 'SUBMISSION' : 'DOCUMENT',
    Icon: <FileIcon height={15} width={16} />
  };

  const viewTag = {
    tag: views,
    Icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={faEye} height={16} width={16} />
      </IconWrapper>
    )
  };

  const downloadTag = {
    tag: downloads,
    Icon: (
      <IconWrapper>
        <FontAwesomeIcon icon={faDownload} height={16} width={16} />
      </IconWrapper>
    )
  };

  return (
    <SubmissionCardLink
      id={`sub${submission?.id}`}
      className="test-ResearchCard"
      key={submission?.id || submissionId}
      href={`/o/article/${submission?.id || submissionId}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => props?.logEvent && props.logEvent()}
      cardWidth={cardWidth}
    >
      <SubmissionCardBase
        key={`${submission?.id}-submission-card`}
        contentTag={contentTag}
        date={updatedAt}
        title={submission?.title || ''}
        authors={submission?.authors || []}
        thumbnailUrl={submission?.thumbUrl || ''}
        views={viewTag}
        isRestricted={submission?.embargoed}
        download={downloadTag}
        authorsHighlightingInformation={submission?.highlightResult?.authors}
        titleHighlightingInformation={submission?.highlightResult?.title}
      />
    </SubmissionCardLink>
  );
};

export default SubmissionCard;
